import { cn } from "@/lib/utils";
import { GRADE_LABEL, type LetterGrade } from "@/lib/amenityGrades";

/**
 * Every grade card sits on the same raised white surface. Grade differentiation
 * lives entirely in the mini medallion (ring + fill + ink), never in a large fill.
 */
export const GRADE_CARD_SURFACE = {
  background: '#FFFFFF',
  border: '#E4D8C6',
} as const;

/** Muted brown used for the small letterspaced feature name. */
export const FEATURE_LABEL_INK = '#7b4f2e';
/** Espresso ink, matching the main Grounded Score medallion number. */
export const ESPRESSO_INK = '#3b2314';

export interface GradeStyle {
  /** Medallion ring color. */
  ringColor: string;
  /** Very light circle fill inside the ring. */
  ringFill: string;
  /** Letter color inside the medallion. */
  ink: string;
  /** Ring thickness in px (default 2). */
  ringWidth?: number;
  ringStyle?: 'solid' | 'dashed';
}

export const GRADE_STYLES: Record<LetterGrade, GradeStyle> = {
  // Brand green, same value as the "Report current conditions" button.
  A: { ringColor: '#4a7c59', ringFill: '#eef4ef', ink: ESPRESSO_INK, ringWidth: 3 },
  // Certification gold token.
  B: { ringColor: 'hsl(var(--seal-gold))', ringFill: '#faf3e2', ink: ESPRESSO_INK },
  C: { ringColor: '#7b4f2e', ringFill: '#f7efe6', ink: ESPRESSO_INK },
  D: { ringColor: '#7b4f2e', ringFill: '#f7efe6', ink: ESPRESSO_INK },
  F: { ringColor: '#9a6b45', ringFill: '#f5f1ec', ink: '#9a6b45' },
  U: { ringColor: '#8a8178', ringFill: '#fbfaf8', ink: '#8a8178', ringStyle: 'dashed' },
};

export interface FeatureGradeCardProps {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  /** Feature name, e.g. "WiFi" */
  label: string;
  grade: LetterGrade;
  /** Factual descriptor that earned the grade, e.g. "Free lot" */
  descriptor: string;
  /** Optional secondary action rendered under the descriptor */
  action?: { label: string; onClick: () => void };
  className?: string;
}

/**
 * A single feature grade card: a mini medallion echoing the Grounded Score
 * seal, a one-word quality label, and the factual descriptor that earned it.
 * Never renders point values or weights.
 */
export function FeatureGradeCard({
  icon: Icon,
  label,
  grade,
  descriptor,
  action,
  className,
}: FeatureGradeCardProps) {
  const s = GRADE_STYLES[grade];
  const isUnverified = grade === 'U';

  return (
    <div
      className={cn("rounded-xl border text-center px-3 py-3.5 flex flex-col items-center", className)}
      style={{
        backgroundColor: GRADE_CARD_SURFACE.background,
        borderColor: GRADE_CARD_SURFACE.border,
        borderWidth: 1,
        borderStyle: 'solid',
      }}
    >
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className="h-3.5 w-3.5" style={{ color: FEATURE_LABEL_INK }} />
        <span
          className="font-bold uppercase tracking-[0.12em] text-[11px]"
          style={{ color: FEATURE_LABEL_INK }}
        >
          {label}
        </span>
      </div>

      <div
        data-testid="grade-medallion"
        className="flex h-14 w-14 items-center justify-center rounded-full"
        style={{
          backgroundColor: s.ringFill,
          borderColor: s.ringColor,
          borderWidth: s.ringWidth ?? 2,
          borderStyle: s.ringStyle ?? 'solid',
        }}
      >
        <span
          className="font-display font-bold leading-none -tracking-[0.03em] text-[1.75rem]"
          style={{ color: s.ink }}
        >
          {isUnverified ? '—' : grade}
        </span>
      </div>

      <span
        className="text-xs font-semibold mt-2"
        style={{ color: isUnverified ? s.ink : ESPRESSO_INK }}
      >
        {GRADE_LABEL[grade]}
      </span>

      {!isUnverified && (
        <span
          className="text-[11px] leading-snug mt-0.5"
          style={{ color: FEATURE_LABEL_INK, opacity: 0.85 }}
        >
          {descriptor}
        </span>
      )}

      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="text-[11px] underline mt-1.5"
          style={{ color: FEATURE_LABEL_INK }}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
